/**
 * ShapeTool.tsx
 * 
 * 职责：
 * 1. 提供形状绘制工具的用户界面组件
 * 2. 管理形状类型(矩形、圆形、三角形、直线)的选择
 * 3. 处理形状的颜色和边框粗细设置
 * 4. 与DrawingService交互，更新形状绘制属性
 * 5. 提供用户友好的形状工具面板
 * 6. 条件渲染工具面板，仅在形状工具激活时显示
 * 7. 同步本地UI状态与DrawingService的形状配置
 */

import React, { useState, useEffect } from 'react';
import { ShapeType } from '../../types';
import { DrawingService } from '../services/DrawingService';

interface ShapeToolProps {
  drawingService: DrawingService;
  isActive: boolean;
  position?: { top: number, left: number };
}

const ShapeTool: React.FC<ShapeToolProps> = ({
  drawingService,
  isActive,
  position
}) => {
  // 使用React状态来跟踪当前选择的形状类型、颜色和粗细
  const [selectedShapeType, setSelectedShapeType] = useState<ShapeType>(
    ShapeType.RECTANGLE
  );
  const [currentColor, setCurrentColor] = useState<string>(drawingService.getShapeColor());
  const [currentSize, setCurrentSize] = useState<number>(drawingService.getShapeSize());
  // 添加动画状态
  const [isVisible, setIsVisible] = useState(false);
  
  // 当组件挂载时同步初始状态并设置直角矩形为默认类型
  useEffect(() => {
    // 设置默认形状类型为直角矩形
    drawingService.setShapeType(ShapeType.RECTANGLE);
    setSelectedShapeType(drawingService.getShapeType());
    setCurrentColor(drawingService.getShapeColor());
    setCurrentSize(drawingService.getShapeSize());
  }, [drawingService]);
  
  // 监听isActive变化，添加延迟显示效果
  useEffect(() => {
    if (isActive) {
      // 延迟一帧显示，确保CSS过渡生效
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 10);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [isActive]);
  
  // 颜色选项
  const colorOptions = [
    '#000000', // 黑色
    '#FF0000', // 红色
    '#0000FF', // 蓝色
    '#00FF00', // 绿色
    '#FFFF00', // 黄色
    '#FF00FF', // 品红
    '#00FFFF', // 青色
  ];
  
  // 线条粗细选项
  const sizeOptions = [1, 2, 3, 5, 8];
  
  // 防止事件冒泡，阻止点击工具面板选项时自动切换回选择工具
  const handlePanelClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // 触发自定义事件
    document.dispatchEvent(new CustomEvent('tool-panel-interaction'));
  };
  
  // 处理形状类型变更
  const handleShapeTypeChange = (type: ShapeType) => {
    setSelectedShapeType(type);
    drawingService.setShapeType(type);
    // 触发自定义事件
    document.dispatchEvent(new CustomEvent('tool-panel-interaction'));
  };
  
  // 处理颜色变更
  const handleColorChange = (color: string) => {
    setCurrentColor(color);
    drawingService.setShapeColor(color);
    // 触发自定义事件
    document.dispatchEvent(new CustomEvent('tool-panel-interaction'));
  };
  
  // 处理粗细变更
  const handleSizeChange = (size: number) => {
    setCurrentSize(size);
    drawingService.setShapeSize(size);
    // 触发自定义事件
    document.dispatchEvent(new CustomEvent('tool-panel-interaction'));
  };
  
  // 计算面板位置
  const panelStyle = position ? {
    top: `${position.top}px`,
    left: `${position.left}px`,
  } : {
    top: '60px',
    left: '80px',
  };
  
  // 如果工具未激活，不显示面板
  if (!isActive) {
    return null;
  }
  
  return (
    <div 
      className="shape-tool-panel" 
      style={{
        position: 'absolute',
        ...panelStyle,
        backgroundColor: '#f5f5f5',
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '10px',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        zIndex: 9000,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(-10px)',
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none'
      }}
      onClick={handlePanelClick}
    >
      <div className="shape-type-selector" style={{ marginBottom: '10px' }}>
        <h4 style={{ marginBottom: '5px', fontSize: '14px' }}>形状类型</h4>
        <div className="shape-types" style={{ 
          display: 'flex', 
          flexWrap: 'wrap',
          gap: '5px'
        }}>
          <button
            onClick={(e) => { handlePanelClick(e); handleShapeTypeChange(ShapeType.RECTANGLE); }}
            tabIndex={-1}
            style={{
              padding: '5px',
              border: selectedShapeType === ShapeType.RECTANGLE ? '2px solid blue' : '1px solid #ccc',
              borderRadius: '3px',
              background: 'white',
              cursor: 'pointer',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title="矩形"
          >
            □
          </button>
          <button
            onClick={(e) => { handlePanelClick(e); handleShapeTypeChange(ShapeType.ROUNDED_RECTANGLE); }}
            tabIndex={-1}
            style={{
              padding: '5px',
              border: selectedShapeType === ShapeType.ROUNDED_RECTANGLE ? '2px solid blue' : '1px solid #ccc',
              borderRadius: '3px',
              background: 'white',
              cursor: 'pointer',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title="圆角矩形"
          >
            ⬭
          </button>
          <button
            onClick={(e) => { handlePanelClick(e); handleShapeTypeChange(ShapeType.CIRCLE); }}
            tabIndex={-1}
            style={{
              padding: '5px',
              border: selectedShapeType === ShapeType.CIRCLE ? '2px solid blue' : '1px solid #ccc',
              borderRadius: '3px',
              background: 'white',
              cursor: 'pointer',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title="圆形"
          >
            ○
          </button>
          <button
            onClick={(e) => { handlePanelClick(e); handleShapeTypeChange(ShapeType.TRIANGLE); }}
            tabIndex={-1}
            style={{
              padding: '5px',
              border: selectedShapeType === ShapeType.TRIANGLE ? '2px solid blue' : '1px solid #ccc',
              borderRadius: '3px',
              background: 'white',
              cursor: 'pointer',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title="三角形"
          >
            △
          </button>
          <button
            onClick={(e) => { handlePanelClick(e); handleShapeTypeChange(ShapeType.LINE); }}
            tabIndex={-1}
            style={{
              padding: '5px',
              border: selectedShapeType === ShapeType.LINE ? '2px solid blue' : '1px solid #ccc',
              borderRadius: '3px',
              background: 'white',
              cursor: 'pointer',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title="直线"
          >
            ╱
          </button>
        </div>
      </div>
      
      <div className="shape-color-selector" style={{ marginBottom: '10px' }}>
        <h4 style={{ marginBottom: '5px', fontSize: '14px' }}>颜色</h4>
        <div className="color-options" style={{ 
          display: 'flex', 
          flexWrap: 'wrap',
          gap: '5px'
        }}>
          {colorOptions.map((color) => (
            <div
              key={color}
              onClick={(e) => { handlePanelClick(e); handleColorChange(color); }}
              style={{
                width: '20px',
                height: '20px',
                backgroundColor: color,
                border: `2px solid ${currentColor === color ? 'blue' : 'transparent'}`,
                borderRadius: '50%',
                cursor: 'pointer',
              }}
              title={color}
            />
          ))}
        </div>
      </div>
      
      <div className="shape-size-selector">
        <h4 style={{ marginBottom: '5px', fontSize: '14px' }}>线条粗细</h4>
        <div className="size-options" style={{ 
          display: 'flex', 
          alignItems: 'center',
          gap: '5px'
        }}>
          {sizeOptions.map((size) => (
            <div
              key={size}
              onClick={(e) => { handlePanelClick(e); handleSizeChange(size); }}
              style={{
                width: size * 3 + 10,
                height: size * 3 + 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: `1px solid ${currentSize === size ? 'blue' : '#ccc'}`,
                borderRadius: '3px',
                cursor: 'pointer',
              }}
              title={`${size}px`}
            >
              <div style={{
                width: '100%',
                height: size,
                backgroundColor: 'black',
              }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ShapeTool; 